"use client";

import { Fragment, type HTMLAttributes, type FC, type Ref } from "react";
import { mergeClasses } from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import type { ClassNameProp } from "../../props";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { useUuid } from "../../hooks/useUuid";

import {
  type containerStyle,
  navigationStyle,
  tabItemStyle,
} from "../TabsNavigation/TabsNavigation.css";

export type TabsRecord = {
  id: string;
  label: string;
  disabled?: boolean;
  active?: boolean;
  tooltip?: string;
};

export type TabsProps = RecipeVariants<typeof containerStyle> &
  ClassNameProp &
  Pick<HTMLAttributes<HTMLDivElement>, "style"> & {
    tabs: TabsRecord[];
    onTabClick?: (tab: TabsRecord) => void;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLUListElement>;
  };

/**
 * Tabs component is used to display a list of tabs with content.
 */
export const Tabs: FC<TabsProps> = ({
  tabs,
  className,
  variant = "default",
  color = "default",
  onTabClick,
  ref,
}) => {
  const uuid = useUuid();

  return (
    <ul
      ref={ref}
      aria-label="Tabs"
      className={mergeClasses(navigationStyle({ variant }), className)}
    >
      {tabs?.map((tab) => (
        <Fragment key={tab.id}>
          <Tooltip value={tab.tooltip} placement="bottom">
            <Text
              as="button"
              role="tab"
              id={`tab-${tab.id}-${uuid}`}
              aria-selected={tab.active}
              aria-controls={`tabpanel-${tab.id}-${uuid}`}
              aria-disabled={tab.disabled}
              {...(tab.active ? { "data-active": "true" } : {})}
              onClick={() => onTabClick?.(tab)}
              className={mergeClasses(
                tabItemStyle({
                  variant,
                  color,
                  disabled: tab.disabled,
                }),
              )}
              disabled={tab.disabled}
            >
              {tab.label}
            </Text>
          </Tooltip>
        </Fragment>
      ))}
    </ul>
  );
};
